// path: frontend/src/components/Staff.jsx
/**
 * Раздел «Персонал». RBAC-флаги на сервере:
 *   - capabilities.editDepartments / editRoles / fireEmployee /
 *     manageAllVacations — глобальные действия;
 *   - vacation.permissions.canManage / canApprove — per-отпуск.
 *
 * Пункты меню сотрудника (Edit / Подразделения / Роли / Уволить)
 * определяются capabilities; окончательно проверяются в
 * EmployeeService и AuthService (fire = fireEmployee, roles = editRoles).
 *
 * Строка сотрудника кликабельна целиком через Clickable as="div":
 * внутри неё может быть FloatingMenu (вложенные <button> запрещены).
 */
import React, { useState, useMemo, useCallback, useRef } from "react";
import { ROLES, VACATION_TYPES, DIALOGS, TOASTS } from "../utils/constants";
import { TODAY, fmtDMY, uid } from "../utils/date";
import { useAuth } from "../hooks/useAuth";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import { useDataHelpers, useFilters } from "../hooks";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { EditEmployeeModal } from "./Modals/EditEmployeeModal";
import { CreateEmployeeModal } from "./Modals/CreateEmployeeModal";
import { CreateDeptModal } from "./Modals/CreateDeptModal";
import { EmployeeCardModal } from "./Modals/EmployeeCardModal";
import Avatar from "./Avatar";
import FloatingMenu from "./FloatingMenu";
import { SearchBox } from "./SearchBox";
import { Select } from "./Select";
import { getPrimaryDeptName, getPositionInDept } from "../utils/helpers";
import { optionsFromMap } from "../utils/selectOptions";
import { isPartTime, effectiveEmployeeRate } from "../utils/employeeRate";
import { ColoredSpan } from "./ColoredSpan";
import { Clickable } from "./Clickable";
import {
  useFireEmployeeMutation,
  useRestoreEmployeeMutation,
} from "../api/queries/employees";
import { useUpsertKbMutation } from "../api/queries/kbs";
import {
  useDeleteVacationMutation,
  useDecideVacationMutation,
} from "../api/queries/vacations";

const INITIAL_FILTERS = Object.freeze({
  query: "",
  kbId: "all",
  deptId: "all",
  role: "all",
});

const ROLE_SELECT_OPTIONS = optionsFromMap(ROLES, "Все роли");

const matchesQuery = (emp, q, departmentsById) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (`${emp.last} ${emp.first}`.toLowerCase().includes(needle)) return true;
  if ((emp.position || "").toLowerCase().includes(needle)) return true;

  const deptText = (emp.departments || [])
    .map((d) => {
      const name = departmentsById.get(d.deptId)?.name || "";
      const pos = d.position || "";
      return `${name} ${pos}`;
    })
    .join(" ")
    .toLowerCase();

  return deptText.includes(needle);
};

const matchesFilters = (emp, filters, departmentsById) => {
  const { query, kbId, deptId, role } = filters;
  const empDepts = emp.departments || [];

  if (role !== "all" && !(emp.roles || []).includes(role)) return false;

  if (deptId === "any") {
    if (empDepts.length === 0) return false;
  } else if (deptId === "none") {
    if (empDepts.length > 0) return false;
  } else if (deptId !== "all") {
    if (!empDepts.some((d) => d.deptId === deptId)) return false;
  }

  if (kbId !== "all") {
    const inAnyKb = empDepts.some((d) => departmentsById.get(d.deptId)?.kbId);
    const isAnyKbChief = (emp.kbIds || []).length > 0;

    if (kbId === "any") {
      if (!inAnyKb && !isAnyKbChief) return false;
    } else if (kbId === "none") {
      if (inAnyKb || isAnyKbChief) return false;
    } else {
      const inKbDept = empDepts.some(
        (d) => departmentsById.get(d.deptId)?.kbId === kbId,
      );
      const isKbChief = (emp.kbIds || []).includes(kbId);
      if (!inKbDept && !isKbChief) return false;
    }
  }

  return matchesQuery(emp, query, departmentsById);
};

const EmployeeRow = React.memo(
  ({
    employee,
    deptId,
    vacationEnd,
    openDepts,
    openRoles,
    fireMutation,
    restoreMutation,
    confirm,
    showToast,
    openEditEmployee,
    departments,
    kbs,
    employees,
  }) => {
    const { capabilities } = useAuth();
    const [cardOpen, setCardOpen] = useState(false);
    const isFired = employee.fired;

    const modalDb = useMemo(
      () => ({ departments, kbs, employees }),
      [departments, kbs, employees],
    );

    const position = useMemo(
      () => getPositionInDept(employee, deptId),
      [employee, deptId],
    );

    const showPartTime = isPartTime(employee);
    const effRate = showPartTime ? effectiveEmployeeRate(employee) : null;

    const handleRowClick = useCallback(() => setCardOpen(true), []);
    const handleCardClose = useCallback(() => setCardOpen(false), []);

    const handleFireToggle = useCallback(async () => {
      const name = `${employee.last} ${employee.first}`;
      const dlg = employee.fired
        ? DIALOGS.restoreEmployee(name)
        : DIALOGS.fireEmployee(name);
      if (!(await confirm(dlg))) return;
      try {
        if (employee.fired) {
          await restoreMutation.mutateAsync(employee.id);
        } else {
          await fireMutation.mutateAsync(employee.id);
        }
      } catch (err) {
        showToast(
          err.message || "Не удалось изменить статус сотрудника",
          "error",
        );
      }
    }, [employee, fireMutation, restoreMutation, confirm, showToast]);

    const buildEmployeeMenu = () => {
      const items = [];

      if (capabilities.editDepartments) {
        items.push({
          id: "edit",
          label: "Редактировать",
          icon: ICONS.edit,
          onClick: () => openEditEmployee(employee.id),
        });
        if (!isFired) {
          items.push({
            id: "depts",
            label: "Подразделения",
            icon: ICONS.users,
            onClick: () => openDepts(employee.id),
          });
        }
      }

      if (capabilities.editRoles && !isFired) {
        items.push({
          id: "roles",
          label: "Роли",
          icon: ICONS.shield,
          onClick: () => openRoles(employee.id),
        });
      }

      if (capabilities.fireEmployee) {
        if (items.length) items.push({ type: "divider" });
        items.push({
          id: "fire",
          label: isFired ? "Восстановить" : "Уволить",
          icon: isFired ? ICONS.restore : ICONS.x,
          danger: !isFired,
          onClick: handleFireToggle,
        });
      }

      return items;
    };

    const menuItems = buildEmployeeMenu();

    return (
      <>
        <Clickable
          as="div"
          className="st-row cursor-pointer"
          onClick={handleRowClick}
        >
          <Avatar employee={employee} size="sm" />
          <div className="st-name">
            <div className="st-fio">
              {employee.last} {employee.first}
              {showPartTime && (
                <span className="pos-tag" title="Эффективная ставка">
                  {effRate} ст.
                </span>
              )}
              {isFired && <span className="vac-badge fired">Уволен</span>}
              {vacationEnd && (
                <span className="vac-badge">
                  в отпуске до {fmtDMY(vacationEnd)}
                </span>
              )}
            </div>
            <div className="st-pos">
              <span>{position.position}</span>
              {position.isExtra && <span className="pos-tag">совм</span>}
            </div>
          </div>
          <div className="st-roles">
            {employee.roles.map((r) => (
              <ColoredSpan
                key={r}
                color={ROLES[r]?.color || "#64748b"}
                className="role-chip role-chip--tinted"
                title={ROLES[r]?.label || r}
              >
                {ROLES[r]?.short || r}
              </ColoredSpan>
            ))}
          </div>

          {menuItems.length > 0 && (
            <FloatingMenu items={menuItems}>
              {({ buttonProps }) => (
                <button
                  {...buttonProps}
                  className="icon-btn"
                  title="Действия"
                  aria-label={`Действия с ${employee.last} ${employee.first}`}
                >
                  <Ic d={ICONS.more} size={16} />
                </button>
              )}
            </FloatingMenu>
          )}
        </Clickable>

        {cardOpen && (
          <EmployeeCardModal
            employee={employee}
            db={modalDb}
            onClose={handleCardClose}
          />
        )}
      </>
    );
  },
);

const VacationRowActions = React.memo(
  ({
    vacation,
    employeeName,
    canManage,
    canApprove,
    onOpen,
    onDelete,
    onApprove,
  }) => {
    const approvingRef = useRef(false);

    const handleApproveClick = useCallback(() => {
      if (approvingRef.current) return;
      approvingRef.current = true;
      try {
        onApprove(vacation);
      } finally {
        queueMicrotask(() => {
          approvingRef.current = false;
        });
      }
    }, [onApprove, vacation]);

    return (
      <>
        {canApprove && (
          <button
            type="button"
            className="icon-btn success"
            onClick={handleApproveClick}
            title="Утвердить отпуск"
            aria-label={`Утвердить отпуск: ${employeeName}`}
          >
            <Ic d={ICONS.check} size={14} />
          </button>
        )}
        {canManage ? (
          <>
            <button
              type="button"
              className="icon-btn"
              onClick={() => onOpen(vacation.id)}
              title="Редактировать"
              aria-label={`Редактировать отпуск: ${employeeName}`}
            >
              <Ic d={ICONS.edit} size={14} />
            </button>
            <button
              type="button"
              className="icon-btn danger"
              onClick={() => onDelete(vacation)}
              title="Удалить"
              aria-label={`Удалить отпуск: ${employeeName}`}
            >
              <Ic d={ICONS.trash} size={14} />
            </button>
          </>
        ) : (
          <button
            type="button"
            className="icon-btn"
            onClick={() => onOpen(vacation.id)}
            title="Просмотр"
            aria-label={`Просмотреть отпуск: ${employeeName}`}
          >
            <Ic d={ICONS.eye} size={14} />
          </button>
        )}
      </>
    );
  },
);

export default function Staff({ db, openRoles, openDepts, openVacation }) {
  const { capabilities } = useAuth();
  const { empName } = useDataHelpers(db);
  const { showToast } = useToast();
  const { prompt, confirm } = useConfirm();

  const fireMutation = useFireEmployeeMutation();
  const restoreMutation = useRestoreEmployeeMutation();
  const upsertKbMutation = useUpsertKbMutation();
  const deleteVacationMutation = useDeleteVacationMutation();
  const decideVacationMutation = useDecideVacationMutation();

  const [showFired, setShowFired] = useState(false);
  const [editEmployeeId, setEditEmployeeId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);

  const departments = useMemo(() => db.departments || [], [db.departments]);
  const kbs = useMemo(() => db.kbs || [], [db.kbs]);

  const { filters, setFilter } = useFilters(INITIAL_FILTERS);
  const { query, kbId, deptId, role } = filters;

  const departmentsById = useMemo(
    () => new Map(departments.map((d) => [d.id, d])),
    [departments],
  );

  const kbSelectOptions = useMemo(
    () => [
      { value: "all", label: "Все сотрудники" },
      { value: "any", label: "Все КБ" },
      ...kbs.map((k) => ({ value: k.id, label: k.name })),
      { value: "none", label: "Вне КБ" },
    ],
    [kbs],
  );

  const deptSelectOptions = useMemo(
    () => [
      { value: "all", label: "Все сотрудники" },
      { value: "any", label: "Все отделы" },
      ...departments.map((d) => ({ value: d.id, label: d.name })),
      { value: "none", label: "Вне отделов" },
    ],
    [departments],
  );

  const vacationEndByEmp = useMemo(() => {
    const map = new Map();
    for (const v of db.vacations) {
      if (v.status !== "approved") continue;
      if (!(v.start <= TODAY && TODAY <= v.end)) continue;
      const prev = map.get(v.empId);
      if (!prev || v.end > prev) map.set(v.empId, v.end);
    }
    return map;
  }, [db.vacations]);

  const filteredActiveEmployees = useMemo(
    () =>
      db.employees.filter(
        (e) => !e.fired && matchesFilters(e, filters, departmentsById),
      ),
    [db.employees, filters, departmentsById],
  );

  const filteredFiredEmployees = useMemo(
    () =>
      db.employees.filter(
        (e) => e.fired && matchesFilters(e, filters, departmentsById),
      ),
    [db.employees, filters, departmentsById],
  );

  const noDeptEmployees = useMemo(() => {
    return filteredActiveEmployees
      .filter(
        (e) =>
          e.departments.length === 0 &&
          !e.roles.includes("kb_chief") &&
          (!e.kbIds || e.kbIds.length === 0),
      )
      .sort((a, b) => {
        if (a.roles.includes("director") && !b.roles.includes("director"))
          return -1;
        if (!a.roles.includes("director") && b.roles.includes("director"))
          return 1;
        return a.last.localeCompare(b.last);
      });
  }, [filteredActiveEmployees]);

  const deptMap = useMemo(() => {
    const map = new Map();
    departments.forEach((d) => {
      const members = filteredActiveEmployees.filter((e) =>
        e.departments.some((x) => x.deptId === d.id),
      );
      map.set(d.id, members);
    });
    return map;
  }, [departments, filteredActiveEmployees]);

  const kbSections = useMemo(() => {
    return kbs
      .map((k) => {
        const allDeptsInKb = departments.filter((d) => d.kbId === k.id);
        const deptsInKb =
          deptId === "all"
            ? allDeptsInKb
            : deptId === "none"
              ? []
              : allDeptsInKb.filter((d) => d.id === deptId);

        const members = filteredActiveEmployees.filter((e) =>
          e.departments.some((d) =>
            allDeptsInKb.some((x) => x.id === d.deptId),
          ),
        );
        const chiefs = filteredActiveEmployees.filter(
          (e) => e.roles.includes("kb_chief") && (e.kbIds || []).includes(k.id),
        );

        const kbOnlyMembers = filteredActiveEmployees.filter(
          (e) =>
            e.departments.length === 0 &&
            !e.roles.includes("kb_chief") &&
            (e.kbIds || []).includes(k.id),
        );

        if (
          deptsInKb.length === 0 &&
          members.length === 0 &&
          chiefs.length === 0 &&
          kbOnlyMembers.length === 0
        )
          return null;

        return { kb: k, deptsInKb, members, chiefs, kbOnlyMembers };
      })
      .filter(Boolean);
  }, [kbs, departments, filteredActiveEmployees, deptId]);

  const deptsWithoutKb = useMemo(() => {
    return departments.filter((d) => {
      if (d.kbId !== null) return false;
      if (deptId === "all") return true;
      return d.id === deptId;
    });
  }, [departments, deptId]);

  const allVacs = useMemo(() => {
    return db.vacations
      .filter((v) => {
        const emp = db.employees.find((e) => e.id === v.empId);
        return (
          emp && !emp.fired && matchesFilters(emp, filters, departmentsById)
        );
      })
      .sort((a, b) => (a.start < b.start ? 1 : -1));
  }, [db.vacations, db.employees, filters, departmentsById]);

  const canFire = Boolean(capabilities.fireEmployee);

  const openEditEmployee = useCallback((id) => setEditEmployeeId(id), []);
  const closeEditEmployee = useCallback(() => setEditEmployeeId(null), []);

  const handleCreateKb = useCallback(async () => {
    const name = await prompt(DIALOGS.createKb);
    if (!name) return;
    try {
      await upsertKbMutation.mutateAsync({
        id: "kb_" + uid(),
        body: { name, full: name },
      });
      showToast(TOASTS.kbCreated(name), "success");
    } catch (err) {
      showToast(err.message || "Не удалось создать КБ", "error");
    }
  }, [prompt, upsertKbMutation, showToast]);

  const renderEmployeeRow = (e, depId) => (
    <EmployeeRow
      key={e.id}
      employee={e}
      deptId={depId}
      vacationEnd={vacationEndByEmp.get(e.id) || null}
      openDepts={openDepts}
      openRoles={openRoles}
      fireMutation={fireMutation}
      restoreMutation={restoreMutation}
      confirm={confirm}
      showToast={showToast}
      openEditEmployee={openEditEmployee}
      departments={departments}
      kbs={kbs}
      employees={db.employees}
    />
  );

  const renderDepartment = (depId) => {
    const members = deptMap.get(depId) || [];
    const dept = departmentsById.get(depId);
    if (!dept) return null;
    const headNames = db.employees
      .filter((e) => (e.headDeptIds || []).includes(depId) && !e.fired)
      .map((e) => `${e.last} ${e.first[0]}.`)
      .join(", ");
    return (
      <div className="st-dept" key={depId}>
        <div className="st-dept-head">
          <span className="st-dept-name">{dept.name}</span>
          <span className="mut">руководитель: {headNames || "-"}</span>
          <span className="kcount">{members.length}</span>
        </div>
        {members.length === 0 ? (
          <div className="mut sm p-2 ml-4">В отделе нет сотрудников</div>
        ) : (
          members.map((e) => renderEmployeeRow(e, depId))
        )}
      </div>
    );
  };

  const handleVacationDelete = useCallback(
    async (vac) => {
      if (
        !(await confirm({
          title: "Удалить отпуск",
          message: `Удалить отпуск ${fmtDMY(vac.start)}–${fmtDMY(vac.end)}?`,
          confirmLabel: "Удалить",
          danger: true,
        }))
      )
        return;
      try {
        await deleteVacationMutation.mutateAsync(vac.id);
      } catch (err) {
        showToast(err.message || "Не удалось удалить отпуск", "error");
      }
    },
    [deleteVacationMutation, showToast, confirm],
  );

  const handleVacationApprove = useCallback(
    async (vacation) => {
      try {
        await decideVacationMutation.mutateAsync({
          id: vacation.id,
          approved: true,
        });
        showToast(TOASTS.vacationApproved, "success");
      } catch (err) {
        showToast(err.message || "Не удалось утвердить отпуск", "error");
      }
    },
    [decideVacationMutation, showToast],
  );

  return (
    <div className="staff">
      <div className="toolbar">
        <SearchBox
          value={query}
          onChange={(v) => setFilter("query", v)}
          placeholder="Поиск по сотруднику, должности, отделу…"
          className="staff-search"
        />
        <Select
          className="filter-select staff-select"
          value={kbId}
          onChange={(v) => setFilter("kbId", v)}
          options={kbSelectOptions}
        />
        <Select
          className="filter-select staff-select"
          value={deptId}
          onChange={(v) => setFilter("deptId", v)}
          options={deptSelectOptions}
        />
        <Select
          className="filter-select"
          value={role}
          onChange={(v) => setFilter("role", v)}
          options={ROLE_SELECT_OPTIONS}
        />
        {capabilities.editRoles && (
          <div className="ml-auto flex gap-2">
            <button className="btn ghost sm" onClick={handleCreateKb}>
              <Ic d={ICONS.plus} size={13} /> КБ
            </button>
            <button
              className="btn ghost sm"
              onClick={() => setShowDeptModal(true)}
            >
              <Ic d={ICONS.plus} size={13} /> Отдел
            </button>
            {capabilities.editDepartments && (
              <button
                className="btn primary sm"
                onClick={() => setShowCreateModal(true)}
              >
                <Ic d={ICONS.plus} size={13} /> Добавить сотрудника
              </button>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateEmployeeModal
          ur={db.employees.find((e) => e.id) || null}
          onClose={() => setShowCreateModal(false)}
          toast={(msg, type) => showToast(msg, type || "error")}
        />
      )}

      {showDeptModal && (
        <CreateDeptModal
          onClose={() => setShowDeptModal(false)}
          toast={(msg, type) => showToast(msg, type || "error")}
        />
      )}

      {editEmployeeId && (
        <EditEmployeeModal
          employeeId={editEmployeeId}
          onClose={closeEditEmployee}
          toast={(msg, type) => showToast(msg, type || "error")}
        />
      )}

      {(query || kbId !== "all" || deptId !== "all" || role !== "all") &&
        filteredActiveEmployees.length === 0 &&
        filteredFiredEmployees.length === 0 && (
          <div className="empty-note p-4">
            Сотрудников по заданным условиям не найдено
          </div>
        )}

      {noDeptEmployees.length > 0 && (
        <div className="st-section">
          <div className="st-sec-head">
            <div className="st-sec-title">Руководство</div>
          </div>
          {noDeptEmployees.map((e) => renderEmployeeRow(e, null))}
        </div>
      )}

      {kbSections.map(({ kb, deptsInKb, chiefs, kbOnlyMembers }) => (
        <div className="st-section" key={kb.id}>
          <div className="st-sec-head">
            <div className="st-sec-title">{kb.name}</div>
          </div>
          {chiefs.length > 0 && (
            <div className="st-dept">
              {chiefs.map((e) => renderEmployeeRow(e, null))}
            </div>
          )}

          {kbOnlyMembers.length > 0 && (
            <div className="st-dept">
              <div className="st-dept-head">
                <span className="st-dept-name">Сотрудники КБ (без отдела)</span>
                <span className="kcount">{kbOnlyMembers.length}</span>
              </div>
              {kbOnlyMembers.map((e) => renderEmployeeRow(e, null))}
            </div>
          )}

          {deptsInKb.map((d) => renderDepartment(d.id))}
        </div>
      ))}

      {deptsWithoutKb.length > 0 && (
        <div className="st-section">
          <div className="st-sec-head">
            <div className="st-sec-title">Отделы и сотрудники вне КБ</div>
            <div className="st-sec-sub">Подразделения прямого подчинения</div>
          </div>
          {deptsWithoutKb.map((d) => renderDepartment(d.id))}
        </div>
      )}

      {capabilities.manageAllVacations && allVacs.length > 0 && (
        <div className="st-section">
          <div className="st-sec-head">
            <div className="st-sec-title">Все отпуска ({allVacs.length})</div>
            <button
              className="btn primary sm"
              onClick={() => openVacation(null, null)}
            >
              <Ic d={ICONS.plus} size={13} /> Отпуск сотруднику
            </button>
          </div>
          <div className="p-3">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th>Отдел</th>
                  <th>Период</th>
                  <th>Тип</th>
                  <th>Статус</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {allVacs.map((v) => {
                  const e = db.employees.find((x) => x.id === v.empId);
                  const canManage = Boolean(v.permissions?.canManage);
                  const canApprove =
                    v.status === "pending" &&
                    Boolean(v.permissions?.canApprove);
                  const employeeName = empName(v.empId);

                  return (
                    <tr key={v.id}>
                      <td>
                        <b>{employeeName}</b>
                      </td>
                      <td>{getPrimaryDeptName(e, db)}</td>
                      <td>
                        {fmtDMY(v.start)} - {fmtDMY(v.end)}
                      </td>
                      <td>{VACATION_TYPES[v.type]}</td>
                      <td>
                        <span className={`st-chip ${v.status}`}>
                          {
                            {
                              pending: "На утверждении",
                              approved: "Утверждён",
                              rejected: "Отклонён",
                            }[v.status]
                          }
                        </span>
                      </td>
                      <td>
                        <VacationRowActions
                          vacation={v}
                          employeeName={employeeName}
                          canManage={canManage}
                          canApprove={canApprove}
                          onOpen={openVacation}
                          onDelete={handleVacationDelete}
                          onApprove={handleVacationApprove}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredFiredEmployees.length > 0 && canFire && (
        <div className="st-section">
          <div className="st-sec-head">
            <div className="st-sec-title">Архив сотрудников (уволенные)</div>
            <button
              className="btn ghost sm"
              onClick={() => setShowFired(!showFired)}
            >
              {showFired ? "Скрыть" : "Показать"}
            </button>
          </div>
          {showFired && (
            <div className="st-empty-box">
              {filteredFiredEmployees.map((e) => renderEmployeeRow(e, null))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
